import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios';
import { GitStackPayload } from '@/react/common/stacks/types';
import { buildStackUrl } from '@/react/common/stacks/queries/buildUrl';
import { queryKeys } from '@/react/common/stacks/queries/query-keys';
import { useCurrentUser } from '@/react/hooks/useUser';
import { saveGitCredentialsIfNeeded } from '@/react/portainer/account/git-credentials/queries/useCreateGitCredentialsMutation';
import { GitAuthModel } from '@/react/portainer/gitops/types';
import { withGlobalError } from '@/react-tools/react-query';

async function updateGitStackSettings(
  stackId: number,
  endpointId: number,
  payload: GitStackPayload
) {
  try {
    const { data } = await axios.post(buildStackUrl(stackId, 'git'), payload, {
      params: { endpointId },
    });
    return data;
  } catch (e) {
    throw parseAxiosError(e as Error);
  }
}

export function useUpdateGitStackSettings() {
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({
      stackId,
      endpointId,
      payload: { SaveCredential, NewCredentialName, ...payload },
    }: {
      stackId: number;
      endpointId: number;
      payload: GitStackPayload & {
        SaveCredential?: boolean;
        NewCredentialName?: string;
      };
    }) => {
      const gitAuthModel: GitAuthModel = {
        RepositoryAuthentication: payload.RepositoryAuthentication,
        RepositoryUsername: payload.RepositoryUsername,
        RepositoryPassword: payload.RepositoryPassword,
        RepositoryGitCredentialID: payload.RepositoryGitCredentialID,
        RepositoryAuthorizationType: payload.RepositoryAuthorizationType,
        SaveCredential,
        NewCredentialName,
      };
      const resolvedAuth = await saveGitCredentialsIfNeeded(
        user.Id,
        gitAuthModel
      );
      return updateGitStackSettings(stackId, endpointId, {
        ...payload,
        RepositoryAuthentication: resolvedAuth.RepositoryAuthentication,
        RepositoryGitCredentialID: resolvedAuth.RepositoryGitCredentialID,
        RepositoryUsername: resolvedAuth.RepositoryUsername,
        RepositoryPassword: resolvedAuth.RepositoryPassword,
        RepositoryAuthorizationType: resolvedAuth.RepositoryAuthorizationType,
      });
    },
    onSuccess: (_, { stackId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stack(stackId),
        exact: true,
      });
    },
    ...withGlobalError('Unable to save stack settings'),
  });
}
